/**
 * Theme Helper Utilities
 *
 * Type-safe utilities for working with the theme system.
 * These helpers enforce proper theme usage and provide common color operations.
 */

import { useThemeColors } from './useThemeColors';
import type { ColorPalette } from './types';

/**
 * Type-safe style factory that enforces theme usage
 *
 * @example
 * const styles = useThemedStyle(colors => ({
 *   container: {
 *     backgroundColor: colors.background,
 *     color: colors.text.primary,
 *   },
 *   button: {
 *     backgroundColor: colors.primary,
 *     borderColor: colors.border.default,
 *   }
 * }));
 */
export function useThemedStyle<T extends Record<string, any>>(
  styleFactory: (colors: ColorPalette) => T
): T {
  const colors = useThemeColors();
  return styleFactory(colors);
}

/**
 * Add opacity to a hex color
 *
 * @param hexColor - Hex color (e.g., '#FA8C16')
 * @param opacity - Opacity value 0-100 (e.g., 20 for 20%)
 * @returns Hex color with opacity (e.g., '#FA8C1633')
 *
 * @example
 * const colors = useThemeColors();
 * const transparentPrimary = withOpacity(colors.primary, 20); // '#FA8C1633'
 * const semiTransparentError = withOpacity(colors.error, 50); // '#F5222D80'
 */
export function withOpacity(hexColor: string, opacity: number): string {
  // Validate opacity range
  if (opacity < 0 || opacity > 100) {
    throw new Error(`Opacity must be between 0-100, got ${opacity}`);
  }

  // Remove # if present
  const hex = hexColor.replace('#', '');

  // Validate hex format
  if (!/^[0-9A-Fa-f]{6}$/.test(hex)) {
    throw new Error(`Invalid hex color: ${hexColor}`);
  }

  // Convert opacity percentage to hex (0-100 -> 00-FF)
  const alpha = Math.round((opacity / 100) * 255);
  const alphaHex = alpha.toString(16).padStart(2, '0');

  return `#${hex}${alphaHex}`;
}

/**
 * Convert hex to rgba for CSS usage
 *
 * @param hexColor - Hex color (e.g., '#FA8C16')
 * @param alpha - Alpha value 0-1 (e.g., 0.2 for 20% opacity)
 * @returns rgba string (e.g., 'rgba(250, 140, 22, 0.2)')
 *
 * @example
 * const colors = useThemeColors();
 * const transparentPrimary = hexToRgba(colors.primary, 0.2); // 'rgba(250, 140, 22, 0.2)'
 *
 * // Use in inline styles
 * <div style={{ backgroundColor: hexToRgba(colors.primary, 0.1) }}>
 */
export function hexToRgba(hexColor: string, alpha: number): string {
  // Validate alpha range
  if (alpha < 0 || alpha > 1) {
    throw new Error(`Alpha must be between 0-1, got ${alpha}`);
  }

  // Remove # if present
  const hex = hexColor.replace('#', '');

  // Validate hex format
  if (!/^[0-9A-Fa-f]{6}$/.test(hex)) {
    throw new Error(`Invalid hex color: ${hexColor}`);
  }

  // Parse RGB values
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Check if a color meets WCAG contrast requirements against a background
 *
 * @param foreground - Foreground color (hex)
 * @param background - Background color (hex)
 * @param level - WCAG level ('AA' or 'AAA')
 * @param size - Text size ('normal' or 'large')
 * @returns Whether the contrast ratio meets requirements
 *
 * @example
 * const colors = useThemeColors();
 * const isAccessible = meetsContrastRequirement(
 *   colors.text.primary,
 *   colors.background,
 *   'AA',
 *   'normal'
 * ); // true if contrast is sufficient
 */
export function meetsContrastRequirement(
  foreground: string,
  background: string,
  level: 'AA' | 'AAA' = 'AA',
  size: 'normal' | 'large' = 'normal'
): boolean {
  const ratio = calculateContrastRatio(foreground, background);

  // WCAG 2.1 requirements
  const requirements = {
    AA: { normal: 4.5, large: 3 },
    AAA: { normal: 7, large: 4.5 },
  };

  return ratio >= requirements[level][size];
}

/**
 * Calculate WCAG contrast ratio between two colors
 *
 * @param color1 - First color (hex)
 * @param color2 - Second color (hex)
 * @returns Contrast ratio (1-21)
 */
function calculateContrastRatio(color1: string, color2: string): number {
  const lum1 = getRelativeLuminance(color1);
  const lum2 = getRelativeLuminance(color2);

  const lighter = Math.max(lum1, lum2);
  const darker = Math.min(lum1, lum2);

  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Calculate relative luminance of a color (WCAG 2.1)
 *
 * @param hexColor - Hex color
 * @returns Relative luminance (0-1)
 */
function getRelativeLuminance(hexColor: string): number {
  // Remove # if present
  const hex = hexColor.replace('#', '');

  // Parse RGB values
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  // Apply gamma correction
  const gammaCorrect = (c: number) => {
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };

  const rLinear = gammaCorrect(r);
  const gLinear = gammaCorrect(g);
  const bLinear = gammaCorrect(b);

  // Calculate luminance
  return 0.2126 * rLinear + 0.7152 * gLinear + 0.0722 * bLinear;
}

/**
 * Get a readable text color (black or white) for any background color
 *
 * @param backgroundColor - Background color (hex)
 * @returns '#FFFFFF' or '#000000' depending on which has better contrast
 *
 * @example
 * const colors = useThemeColors();
 * const textColor = getReadableTextColor(colors.primary); // '#FFFFFF' or '#000000'
 */
export function getReadableTextColor(backgroundColor: string): '#FFFFFF' | '#000000' {
  const whiteContrast = calculateContrastRatio(backgroundColor, '#FFFFFF');
  const blackContrast = calculateContrastRatio(backgroundColor, '#000000');

  return whiteContrast > blackContrast ? '#FFFFFF' : '#000000';
}

/**
 * Create a CSS custom property (CSS variable) name from a theme color key
 *
 * @param colorKey - Color key from palette (e.g., 'text.primary')
 * @returns CSS variable name (e.g., '--color-text-primary')
 *
 * @example
 * toCssVar('text.primary') // '--color-text-primary'
 * toCssVar('primary') // '--color-primary'
 */
export function toCssVar(colorKey: string): string {
  return `--color-${colorKey.replace(/\./g, '-')}`;
}
